import { ComponentProps, ReactElement, createContext, useContext } from 'react';

import type { DialogLazyComponentsType, DialogType } from './DialogManager';
import type { ManagedAxleDialogProps } from './ManagedAxleDialog';

// Kept in a separate file so createContext() is never re-executed during HMR.
// All imports from ./DialogManager are type-only (erased at runtime), so this
// module has no runtime dep on DialogManager and won't be invalidated by HMR
// when dialog files change.

export interface IDialogOptions {
    closeOnNavigationChange?: boolean;
}

export interface IActiveDialog {
    id: string;
    type: DialogType;
    component: () => ReactElement;
    options: IDialogOptions;
}

export interface IDialogActionsContext {
    openDialog: <T extends DialogType>(
        type: T,
        props: Omit<ComponentProps<DialogLazyComponentsType[T]>, keyof ManagedAxleDialogProps>,
    ) => void;
}

// The actions (openDialog) and the active-dialog state live in separate
// contexts on purpose. openDialog is a stable callback, while activeDialogs
// changes every time a dialog opens or closes. Keeping them apart means the
// many components that only call `useDialog()` to open dialogs (e.g. the
// schedule calendar) do not re-render when a dialog is opened — only the few
// components that actually read the active-dialog state do.
export const DialogActionsContext = createContext<IDialogActionsContext | null>(null);
export const DialogStateContext = createContext<IActiveDialog[] | null>(null);

export const useDialog = () => {
    const context = useContext(DialogActionsContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};

export const useActiveDialogs = () => {
    const context = useContext(DialogStateContext);
    if (context === null) {
        throw new Error('useActiveDialogs must be used within a DialogProvider');
    }
    return context;
};
